import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
} from './alert-dialog';
import { cn } from '../../lib/utils';

/**
 * ConfirmDialog — DESIGN_SYSTEM.md §7/§8.
 * Tone icon badge + title + consequence, with optional reason chips + free-text
 * "Motivo". Cancel + tone-colored confirm. `onConfirm` receives the reason.
 * Use for every destructive action (Suspend, Cancel, Remove, Reject).
 */
export type ConfirmTone = 'destructive' | 'warning' | 'indigo';

const TONE: Record<ConfirmTone, { badge: string; icon: string; button: string }> = {
  destructive: {
    badge: 'bg-error-bg text-error',
    icon: 'text-error',
    button: 'bg-error text-white hover:bg-error/90',
  },
  warning: {
    badge: 'bg-warning-bg text-warning',
    icon: 'text-warning',
    button: 'bg-warning text-white hover:bg-warning/90',
  },
  indigo: {
    badge: 'bg-indigo-tint text-indigo',
    icon: 'text-indigo',
    button: 'bg-indigo text-white hover:bg-indigo-press',
  },
};

export type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  consequence?: string;
  tone?: ConfirmTone;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: (reason?: string) => void | Promise<void>;
  isLoading?: boolean;
  /** Require a "Motivo" before confirm is enabled. */
  reasonRequired?: boolean;
  /** Show the reason textarea (implied true when reasonRequired). */
  withReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  /** Max characters for the reason textarea (mirror the backend limit). */
  reasonMaxLength?: number;
  /** Min characters (trimmed) before a required reason can confirm (mirror the backend limit). */
  reasonMinLength?: number;
  /** Quick-pick reason chips that fill the textarea. */
  reasonChips?: string[];
  icon?: React.ComponentType<{ className?: string }>;
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  consequence,
  tone = 'destructive',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  isLoading = false,
  reasonRequired = false,
  withReason = false,
  reasonLabel = 'Motivo',
  reasonPlaceholder = 'Add a reason…',
  reasonMaxLength,
  reasonMinLength,
  reasonChips,
  icon,
}: ConfirmDialogProps) {
  const [reason, setReason] = React.useState('');
  const styles = TONE[tone];
  const showReason = withReason || reasonRequired || Boolean(reasonChips?.length);
  const Icon = icon ?? (tone === 'indigo' ? Info : AlertTriangle);

  React.useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const minLength = Math.max(1, reasonMinLength ?? 1);
  const reasonTooShort = reasonRequired && reason.trim().length < minLength;
  const canConfirm = !isLoading && !reasonTooShort;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    await onConfirm(showReason ? reason.trim() : undefined);
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-card border-hairline shadow-latio-md sm:max-w-md">
        <div className="flex gap-3">
          <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-pill', styles.badge)}>
            <Icon className={cn('size-5', styles.icon)} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-[17px] font-bold text-ink">{title}</h2>
            {consequence && <p className="mt-1 text-sm text-ink-2">{consequence}</p>}

            {showReason && (
              <div className="mt-3 space-y-2">
                {reasonChips && reasonChips.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {reasonChips.map((chip) => {
                      const selected = reason === chip;
                      return (
                        <button
                          key={chip}
                          type="button"
                          onClick={() => setReason(selected ? '' : chip)}
                          className={cn(
                            'rounded-pill border px-2.5 py-1 text-[13px] font-medium transition-colors duration-150',
                            selected
                              ? 'border-indigo bg-indigo-tint text-indigo'
                              : 'border-hairline text-ink-2 hover:text-ink',
                          )}
                        >
                          {chip}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div>
                  <label className="mb-1 block text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-2">
                    {reasonLabel}
                    {reasonRequired && <span className="text-error"> *</span>}
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={reasonPlaceholder}
                    maxLength={reasonMaxLength}
                    rows={3}
                    className="w-full resize-none rounded-input border border-hairline bg-background px-3 py-2 text-sm text-ink outline-none placeholder:text-ink-3 focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  {reasonRequired && reasonMinLength && reasonMinLength > 1 && (
                    <p className={cn('mt-1 text-[12px]', reasonTooShort ? 'text-ink-3' : 'text-success')}>
                      At least {reasonMinLength} characters ({reason.trim().length}/{reasonMinLength})
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <AlertDialogFooter className="mt-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="inline-flex h-9 items-center justify-center rounded-pill border border-hairline px-4 text-sm font-semibold text-ink transition-colors duration-150 hover:bg-surface-2 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className={cn(
              'inline-flex h-9 items-center justify-center rounded-pill px-4 text-sm font-semibold transition-colors duration-150 disabled:opacity-50',
              styles.button,
            )}
          >
            {isLoading ? 'Processing…' : confirmLabel}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
